'use client';

import { DataGridPremium, GridColDef, useGridApiRef, useKeepGroupedColumnsHidden } from '@mui/x-data-grid-premium';
import { useLazyQuery, useMutation, useQuery } from '@parkchirp/api-client';
import { ResourceTypes } from 'parkchirp-shared/enums';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { CommonContext } from '../../../contexts';
import { DELETE_CONTRACT_DOCUMENT } from '../../../mutations';
import { CONTRACT_DOCUMENTS_SIGNED_URL, GET_CONTRACT_DOCUMENTS } from '../../../queries';
import { ContractDocumentsRow, SetShowDialog, SetShowSnackbar, SnackbarParams } from '../../../types';
import { datagridFilterPanelAttributes, isOwnerPortal } from '../../../utils';
import { Snackbar } from '../../alerts';
import { AuditHistoryModal } from '../../audit';
import { DatagridLinearProgress } from '../../loading';
import { Dialog } from '../../modals';
import { CustomToolbar } from '../../toolbar';
import { AddContractDocumentButton } from './addContractDocumentButton';
import { getContractDocumentsColumns } from './contractDocumentsColumns';
import { ContractDocumentsModal } from './contractDocumentsModal';

type ContractDocumentsTableProps = {
	showDialog?: ReactNode | null;
	setShowDialog?: SetShowDialog;
	setShowSnackbar?: SetShowSnackbar;
	showSnackbar?: Partial<SnackbarParams>;
	initialData?: any;
	theme?: any;
};

export const ContractDocumentsTable = ({
	setShowDialog: setShowDialogDefault = null,
	showDialog: showDialogDefault = null,
	setShowSnackbar: setShowSnackbarDefault = null,
	showSnackbar: showSnackbarDefault = null,
	initialData = [],
	theme,
}: ContractDocumentsTableProps) => {
	const [rows, setRows] = useState<ContractDocumentsRow[]>(initialData);
	const apiRef = useGridApiRef();
	const [deleteDocument, { loading: deleteLoading }] = useMutation(DELETE_CONTRACT_DOCUMENT);
	const [getSignedUrl] = useLazyQuery(CONTRACT_DOCUMENTS_SIGNED_URL);
	const [showSnackbar, setShowSnackbar = setShowSnackbarDefault] = useState(showSnackbarDefault);
	const [showDialog, setShowDialog = setShowDialogDefault] = useState(showDialogDefault);
	const [loading, setLoading] = useState<boolean>(!initialData.length);

	const { hasComponentAccess } = useContext(CommonContext);

	const { data: contractDocuments, refetch } = useQuery(GET_CONTRACT_DOCUMENTS, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		notifyOnNetworkStatusChange: true,
		ssr: false,
	});

	useEffect(() => {
		if (contractDocuments?.getContractDocuments) {
			setRows(contractDocuments.getContractDocuments);
			setLoading(false);
		}
	}, [contractDocuments]);

	const showModal = useCallback(
		(editRow) =>
			setShowDialog(
				<ContractDocumentsModal
					refetch={refetch}
					editRow={editRow || null}
					handleClose={() => setShowDialog(null)}
					getSignedUrl={getSignedUrl}
					setShowSnackbar={setShowSnackbar}
					theme={theme}
				/>,
			),
		[setShowDialog, refetch, getSignedUrl, setShowSnackbar, theme],
	);

	const handleAuditHistory = async (contractDocumentId: string, timezone: string) => {
		const auditDialog = (
			<AuditHistoryModal
				title='Contract Document Audit History'
				resourceId={contractDocumentId}
				resourceType={ResourceTypes.CONTRACT_DOCUMENT}
				timezone={timezone}
				onClose={() => {
					setShowDialog(null);
				}}
			/>
		);

		setShowDialog(auditDialog);
	};

	const columns: GridColDef[] = getContractDocumentsColumns({
		showModal,
		rows,
		setRows,
		deleteDocument,
		setShowSnackbar,
		setShowDialog,
		getSignedUrl,
		theme,
		hasComponentAccess,
		handleAuditHistory,
	});

	const initialState = useKeepGroupedColumnsHidden({
		apiRef,
		initialState: {
			rowGrouping: {
				model: ['accountId'],
			},
		},
	});

	return (
		<div className='pca__datagrid-sticky--headfoot'>
			<DataGridPremium
				sx={{ height: '100%' }}
				disableRowSelectionOnClick
				apiRef={apiRef}
				rows={!loading && rows ? rows : []}
				columns={columns}
				columnVisibilityModel={{ actions: !isOwnerPortal }}
				initialState={initialState}
				loading={loading || deleteLoading}
				slots={{
					toolbar: () => (
						<CustomToolbar
							customAction={
								isOwnerPortal ? undefined : <AddContractDocumentButton showModal={showModal} />
							}
						/>
					),
					loadingOverlay: DatagridLinearProgress,
				}}
				slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
				data-cy='contract-docs-table-container'
			/>
			{!showSnackbarDefault && <Snackbar showSnackbar={showSnackbar} setShowSnackbar={setShowSnackbar} />}
			{!showDialogDefault && <Dialog showDialog={showDialog} setShowDialog={setShowDialog} />}
		</div>
	);
};
